/**
 * Strips anything executable from HTML before it is rendered into a preview
 * iframe. Shared by the admin template editor and the Sales RM wizard preview so
 * the two can never drift.
 *
 * Defence in depth for `sandbox="allow-same-origin"`. Both previews grant the
 * frame this app's origin purely so their height can be measured, which is safe
 * ONLY because `allow-scripts` is absent - the two flags together let a frame
 * remove its own sandbox and reach the parent DOM and its tokens. The body is
 * admin-authored template HTML (a raw block can contain anything), so that
 * isolation currently rests on a single missing attribute that a future edit
 * could add without realising why it matters.
 *
 * Removing the obvious executable surface here raises the bar. Nothing
 * legitimate is lost: a real email can never contain script - every mail client
 * strips it - so any script in a template is either a mistake or an attack.
 *
 * WHAT THIS IS NOT. It is a hardening pass, not a sanitiser, and it does NOT
 * make `allow-scripts` safe to add. It misses, at least: entity- and
 * whitespace-obfuscated `javascript:` URLs (`java&#115;cript:`, `jav\tascript:`),
 * `<iframe srcdoc>`, `<object>`, `<embed>`, `<svg><script>`, and
 * `<meta http-equiv="refresh">`. The security boundary is the SANDBOX - the
 * absence of `allow-scripts` next to `allow-same-origin` - and this function only
 * reduces what is sitting behind it.
 *
 * If untrusted HTML ever needs to be genuinely safe (injected into the parent
 * document, or framed with scripts enabled), use a real sanitiser such as
 * DOMPurify. Do not extend this regex chain and call it done.
 */
export const stripExecutable = (html: string): string =>
  html
    // <script>...</script>, including unclosed/malformed openers.
    .replace(/<script\b[\s\S]*?<\/script\s*>/gi, '')
    .replace(/<script\b[^>]*>/gi, '')
    // Inline handlers: on*="...", on*='...', and bare on*=value.
    .replace(/\son[a-z]+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '')
    // javascript: URLs in href/src/action.
    .replace(/javascript:/gi, '');

export default stripExecutable;
